import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { FileText, Plus, Search, Copy, Edit, Mail, Bell, Share } from 'lucide-react';

const MarketingTemplates = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [templates, setTemplates] = useState([
    {
      id: 1,
      name: 'New Member Welcome Email',
      category: 'Email',
      description: 'Welcome email template for new coworking space members',
      lastModified: '2024-01-15',
      content: `Subject: Welcome to [Coworking Space Name]!

Dear [Member Name],

Welcome to our vibrant coworking community! We're thrilled to have you join us and are excited to support your professional journey.

Here's what you need to know to get started:

🔑 Access Information:
- Your access card is ready for pickup at reception
- Building hours: Monday-Friday 8AM-8PM, Weekends 9AM-6PM
- WiFi Network: [Network Name] | Password: [Password]

🎯 Member Benefits:
- 24/7 access to your dedicated workspace
- Unlimited coffee, tea, and refreshments
- Access to meeting rooms (bookable via our app)
- Free printing (up to 50 pages/month)
- Networking events every Thursday evening

📱 Important Links:
- Member Portal: [Portal Link]
- Booking System: [Booking Link]
- Community Slack: [Slack Invite]

If you have any questions, don't hesitate to reach out to our community manager at [Email] or visit our front desk.

Welcome aboard!

Best regards,
The [Coworking Space Name] Team`
    },
    {
      id: 2,
      name: 'Event Notification Template',
      category: 'Notification',
      description: 'Template for upcoming community events and workshops',
      lastModified: '2024-01-12',
      content: `🎉 Exciting Event Coming Up at [Coworking Space Name]!

Event: [Event Name]
Date: [Event Date]
Time: [Event Time]
Location: [Event Location]

[Event Description]

This is a great opportunity to:
✨ Network with fellow entrepreneurs
✨ Learn new skills
✨ Enjoy complimentary refreshments
✨ Expand your professional circle

RSVP Required: [RSVP Link]
Limited Spots Available!

Questions? Contact us at [Contact Email]

See you there!
[Coworking Space Name] Team`
    },
    {
      id: 3,
      name: 'Lease Renewal Reminder',
      category: 'Email',
      description: 'Friendly reminder for upcoming lease renewals',
      lastModified: '2024-01-10',
      content: `Subject: Your Lease Renewal - [Office Number]

Dear [Tenant Name],

Hope this message finds you well! We wanted to reach out regarding your office lease at [Coworking Space Name].

Lease Details:
- Office: [Office Number]
- Current Lease End Date: [End Date]
- Monthly Rent: $[Amount]

We'd love to continue having you as part of our community! Please let us know your intentions regarding lease renewal by [Response Date].

Renewal Options:
- 12-month lease: [Rate]
- 6-month lease: [Rate]
- Month-to-month: [Rate]

If you have any questions or would like to discuss terms, please don't hesitate to contact us at [Contact Info].

Thank you for being a valued member of our community!

Best regards,
[Property Manager Name]
[Coworking Space Name]`
    },
    {
      id: 4,
      name: 'Maintenance Notice Template',
      category: 'Notification',
      description: 'Template for maintenance and building updates',
      lastModified: '2024-01-08',
      content: `🔧 Scheduled Maintenance Notice

Dear [Coworking Space Name] Members,

We will be performing scheduled maintenance in the following area:

Area: [Maintenance Area]
Date: [Maintenance Date]
Time: [Maintenance Time]
Expected Duration: [Duration]

What to Expect:
- [Impact Description]
- [Alternative Arrangements if any]
- [Contact Information for Issues]

We apologize for any inconvenience and appreciate your understanding as we work to maintain our facilities in top condition.

For urgent matters during maintenance, please contact:
Emergency Contact: [Emergency Number]
Email: [Emergency Email]

Thank you for your patience!

Facilities Management Team
[Coworking Space Name]`
    },
    {
      id: 5,
      name: 'Holiday Hours Notification',
      category: 'Notification',
      description: 'Template for holiday schedule announcements',
      lastModified: '2024-01-05',
      content: `🎄 Holiday Hours & Schedule Update

Dear [Coworking Space Name] Community,

As we approach the holiday season, please note our modified schedule:

Holiday Schedule:
- [Holiday Name]: [Date] - [Hours/Closed]
- [Holiday Name]: [Date] - [Hours/Closed]
- [Holiday Name]: [Date] - [Hours/Closed]

Important Information:
🔑 Access cards will remain active for 24/7 members
📞 Emergency contact: [Emergency Contact]
📧 Non-urgent matters: [Email]
🏢 Normal operations resume: [Date]

Special Holiday Arrangements:
- Coffee service: [Status]
- Reception desk: [Hours]
- Mail collection: [Schedule]
- Cleaning services: [Schedule]

We wish you and your families a wonderful holiday season!

Warm regards,
[Coworking Space Name] Team`
    },
    {
      id: 6,
      name: 'Monthly Newsletter Template',
      category: 'Email',
      description: 'Monthly community newsletter template',
      lastModified: '2024-01-03',
      content: `Subject: [Month] Newsletter - [Coworking Space Name]

Hello Amazing Community!

Welcome to our [Month] newsletter! Here's what's happening in our coworking family:

🎉 COMMUNITY HIGHLIGHTS
- [Member Spotlight/Achievement]
- [New Member Introductions]
- [Community Milestones]

📅 UPCOMING EVENTS
- [Event 1]: [Date & Time]
- [Event 2]: [Date & Time] 
- [Event 3]: [Date & Time]

💡 SPACE UPDATES
- [New Amenities/Changes]
- [Policy Updates]
- [Improvement Projects]

🏆 MEMBER ACHIEVEMENTS
- [Member Achievement 1]
- [Member Achievement 2]
- [Member Achievement 3]

📢 ANNOUNCEMENTS
- [Important Announcement 1]
- [Important Announcement 2]

🤝 NETWORKING OPPORTUNITIES
- [Networking Event Details]
- [Collaboration Opportunities]

As always, thank you for making our community so special!

Best,
The [Coworking Space Name] Team`
    }
  ]);

  const [newTemplate, setNewTemplate] = useState({
    name: '',
    category: 'Email',
    description: '',
    content: ''
  });

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || template.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Email': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'Notification': return 'bg-green-100 text-green-800 border-green-300';
      case 'Social': return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'Document': return 'bg-orange-100 text-orange-800 border-orange-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Email': return Mail;
      case 'Notification': return Bell;
      case 'Social': return Share;
      case 'Document': return FileText;
      default: return FileText;
    }
  };

  const addNewTemplate = () => {
    const template = {
      id: templates.length + 1,
      name: newTemplate.name,
      category: newTemplate.category,
      description: newTemplate.description,
      lastModified: new Date().toISOString().split('T')[0],
      content: newTemplate.content
    };
    setTemplates([...templates, template]);
    setNewTemplate({ name: '', category: 'Email', description: '', content: '' });
  };

  const copyToClipboard = (content) => {
    navigator.clipboard.writeText(content);
    // You could add a toast notification here
    alert('Template copied to clipboard!');
  };

  const categoryCounts = templates.reduce((acc, template) => {
    acc[template.category] = (acc[template.category] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Marketing Templates</h1>
          <p className="text-gray-600 mt-2">Manage email and notification templates</p>
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              New Template
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create New Template</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Template Name</Label>
                  <Input
                    id="name"
                    value={newTemplate.name}
                    onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})}
                    placeholder="Enter template name"
                  />
                </div>
                <div>
                  <Label htmlFor="category">Category</Label>
                  <Select value={newTemplate.category} onValueChange={(value) => setNewTemplate({...newTemplate, category: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Email">Email</SelectItem>
                      <SelectItem value="Notification">Notification</SelectItem>
                      <SelectItem value="Social">Social Media</SelectItem>
                      <SelectItem value="Document">Document</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={newTemplate.description}
                  onChange={(e) => setNewTemplate({...newTemplate, description: e.target.value})}
                  placeholder="Brief description of the template"
                />
              </div>
              <div>
                <Label htmlFor="content">Template Content</Label>
                <Textarea
                  id="content"
                  value={newTemplate.content}
                  onChange={(e) => setNewTemplate({...newTemplate, content: e.target.value})}
                  placeholder="Enter your template content here..."
                  rows={12}
                  className="font-mono text-sm"
                />
              </div>
              <Button onClick={addNewTemplate} className="w-full">Create Template</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6 text-center">
            <Mail className="w-8 h-8 mx-auto mb-2 text-blue-600" />
            <div className="text-2xl font-bold text-gray-900">{categoryCounts.Email || 0}</div>
            <div className="text-sm text-gray-600">Email Templates</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 text-center">
            <Bell className="w-8 h-8 mx-auto mb-2 text-green-600" />
            <div className="text-2xl font-bold text-gray-900">{categoryCounts.Notification || 0}</div>
            <div className="text-sm text-gray-600">Notifications</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 text-center">
            <Share className="w-8 h-8 mx-auto mb-2 text-purple-600" />
            <div className="text-2xl font-bold text-gray-900">{categoryCounts.Social || 0}</div>
            <div className="text-sm text-gray-600">Social Media</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 text-center">
            <FileText className="w-8 h-8 mx-auto mb-2 text-orange-600" />
            <div className="text-2xl font-bold text-gray-900">{templates.length}</div>
            <div className="text-sm text-gray-600">Total Templates</div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="Email">Email</SelectItem>
                <SelectItem value="Notification">Notification</SelectItem>
                <SelectItem value="Social">Social Media</SelectItem>
                <SelectItem value="Document">Document</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredTemplates.map((template) => {
          const CategoryIcon = getCategoryIcon(template.category);
          return (
            <Card key={template.id} className="hover:shadow-lg transition-shadow duration-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <CategoryIcon className="w-5 h-5 text-gray-600" />
                    <CardTitle className="text-lg">{template.name}</CardTitle>
                  </div>
                  <Badge className={getCategoryColor(template.category)}>
                    {template.category}
                  </Badge>
                </div>
                <p className="text-sm text-gray-600">{template.description}</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-700 font-mono leading-relaxed">
                      {template.content.substring(0, 200)}...
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>Last modified: {new Date(template.lastModified).toLocaleDateString()}</span>
                  </div>
                  
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(template.content)}
                      className="flex-1"
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      Copy
                    </Button>
                    <Button variant="outline" size="sm" className="flex-1">
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default MarketingTemplates;